import { ServiceConfig } from '@user/store/services/config'
import { get } from '@user/store/services/crud'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ENTITY_TYPES, LocationDTO, MeasureDTO } from '@sales-center/common'
import { transformMeasureToLocation } from '../functions/functions'

export type Status = 'pending' | 'success' | 'fail' | 'idle'

const NoContent = 204

export function useGetPropertyDetails(
  address?: string,
  entityType?: ENTITY_TYPES,
  entityId?: number,
  locations?: LocationDTO[],
) {
  const [status, setStatus] = useState<Status>('idle')
  const [data, setData] = useState<LocationDTO[] | undefined>()

  useEffect(() => {
    setStatus('idle')
    setData(undefined)
  }, [address, entityType, entityId])

  const locationPath =
    entityType === ENTITY_TYPES.Lead
      ? ServiceConfig.location.lead
      : ServiceConfig.location.opp

  const isEntity = !!entityId

  const baseurl = isEntity
    ? `${ServiceConfig.baseAPIUrl}/${locationPath}/${entityId}/locationsWithPropertyDetails`
    : `${ServiceConfig.baseAPIUrl}${ServiceConfig.measureAI.measureAI}${ServiceConfig.measureAI.propertyDetails}?address=${encodeURIComponent(address ?? '')}`

  const modifiedLocations = useMemo(
    () => locations,
    [JSON.stringify(locations)],
  )

  useEffect(() => {
    setStatus('idle')
    setData(undefined)
  }, [address, entityType, entityId, modifiedLocations])

  const getPropertyDetails = useCallback(async () => {
    setStatus('pending')

    const response = await get(baseurl)
    const result = response.status === NoContent ? null : await response.json()

    const returnedLocations: LocationDTO[] = []

    if (!response.ok) {
      setStatus('fail')
    } else if (isEntity) {
      returnedLocations.push(...(result as LocationDTO[]))
      setStatus('success')
    } else {
      const transformed = transformMeasureToLocation(result as MeasureDTO)
      returnedLocations.push(transformed)
      setStatus('success')
    }

    if (locations && locations.length > 0) {
      const existingLocations = returnedLocations.filter((loc) =>
        locations.some((rLoc) => rLoc.id === loc.id),
      )

      setData(existingLocations)
    } else {
      setData(returnedLocations)
    }
  }, [
    address,
    entityId,
    entityType,
    isEntity,
    baseurl,
    modifiedLocations,
    locations,
  ])

  return { status, data, getPropertyDetails }
}
